import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Session, User } from '@supabase/supabase-js';
import { getSupabase } from '@/services/supabaseService';
import { useThemeStore } from '@/stores/ui';
import { useBillingStore } from '@/stores/billing';
import { clearQuotaCache } from '@/composables/useQuotaGate';
import { trackEvent } from '@/boot/analytics';
import { captureEvent, identifyMember, resetIdentity } from '@/boot/posthog';

/**
 * Ceiling on how long a protected-route navigation waits for the initial
 * session restore. Supabase retries a failed token refresh with exponential
 * backoff for ~30s per cycle, so an unreachable auth endpoint would otherwise
 * hold the guard for the whole ladder.
 */
const RESTORE_TIMEOUT_MS = 8_000;

/** Rejects if `promise` has not settled within `ms`. */
const withTimeout = async <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('supabase auth restore timed out')), ms);
      })
    ]);
  } finally {
    clearTimeout(timer);
  }
};

export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null);
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!session.value);

  const setSession = (next: Session | null) => {
    const previousUserId = user.value?.id ?? null;
    const nextUserId = next?.user?.id ?? null;
    session.value = next;
    user.value = next?.user ?? null;

    // admin-vip-unlimited-access (review P2): account-scoped entitlements must
    // not survive an identity change. signOut() routes through here with
    // `null`, and a sign-in from the same tab routes through here with the new
    // user — neither reloads the page, so nothing else would clear the flag.
    // Compared on the user id: a token refresh keeps the same identity and
    // must NOT drop a status that was read correctly.
    if (nextUserId !== previousUserId) {
      useBillingStore().resetVipStatus();
      // Review C1: the quota cache is keyed by workspace + resource, never by
      // the caller, while `check_quota` answers for `auth.uid()`. Two accounts
      // on the same workspace must not share a verdict — least of all an
      // unlimited one. Cleared on the same identity-change condition, so a
      // token refresh (same id) keeps the cache warm.
      clearQuotaCache();
    }
    // D1 — sync theme/locale preferences between localStorage and members.*.
    const ui = useThemeStore();
    if (next?.user) {
      void ui.hydrateFromMember(next.user.id);
      // CA-6: identify the member in PostHog (plan unknown until backend exposes it — D1).
      identifyMember(next.user.id, { email: next.user.email, plan: 'unknown' });
    } else {
      ui.clearRemotePersistence();
    }
  };

  const signIn = async (email: string, password: string): Promise<boolean> => {
    loading.value = true;
    error.value = null;
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) {
        error.value = err.message;
        return false;
      }
      setSession(data.session);
      trackEvent('sign_in');
      // M10 — update last_activity_at for trial inactivity tracking
      if (data.session?.user?.id) {
        supabase
          .from('members')
          .update({ last_activity_at: new Date().toISOString() })
          .eq('user_id', data.session.user.id)
          .then(() => { /* fire-and-forget */ })
          .catch(() => { /* non-critical */ });
      }
      return true;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Sign up with email/password. Optionally pass captchaToken (hCaptcha, D6)
   * which Supabase forwards to hCaptcha for server-side verification.
   */
  const signUp = async (email: string, password: string, captchaToken?: string): Promise<boolean> => {
    loading.value = true;
    error.value = null;
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase.auth.signUp({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined
      });
      if (err) {
        error.value = err.message;
        return false;
      }
      setSession(data.session);
      trackEvent('sign_up');
      captureEvent('signup_completed', { method: 'email' });
      return true;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Sign in via an OAuth provider (Google).
   *
   * @param provider     - Only providers wired through the kds UI.
   * @param redirectTo   - Absolute URL to land on after the OAuth round-trip.
   *                       When omitted, the store falls back to
   *                       `${window.location.origin}/app/dashboard` (D4).
   *                       In a non-browser context (SSR / Node tests) the
   *                       fallback is skipped and Supabase uses the Site URL
   *                       configured in its console.
   *
   * The session is NOT set synchronously — it arrives later through
   * `onAuthStateChange` once the provider redirects back to the app.
   */
  const signInWithProvider = async (
    provider: 'google',
    redirectTo?: string
  ): Promise<boolean> => {
    error.value = null;
    // D4 — default redirect computed in the store (SSR-safe).
    let effectiveRedirect = redirectTo;
    if (!effectiveRedirect && typeof window !== 'undefined') {
      effectiveRedirect = `${window.location.origin}/app/dashboard`;
    }
    const supabase = getSupabase();
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: effectiveRedirect ? { redirectTo: effectiveRedirect } : undefined
    });
    if (err) {
      error.value = err.message;
      return false;
    }
    // Session arrives later through onAuthStateChange (post-redirect).
    return true;
  };

  const signOut = async () => {
    const supabase = getSupabase();
    await supabase.auth.signOut();
    // H10: reset PostHog identity BEFORE setSession(null) so queued events flush under current identity.
    resetIdentity();
    setSession(null);
  };

  /** True once the initial restore settled — succeeded, failed, or timed out. */
  const sessionRestored = ref(false);

  /** Shared promise so boot and the router guard await the same restore. */
  let restorePromise: Promise<void> | null = null;

  /**
   * Restore the persisted session and keep the store in sync from then on.
   *
   * Idempotent: every caller awaits the same promise. Deliberately *not*
   * awaited at boot time — `supabase.auth.getSession()` waits on the client's
   * internal init, which retries a failed token refresh with exponential
   * backoff, so an unreachable auth endpoint (the 521 / CORS case) would hold
   * back `app.mount()` and therefore hydration for tens of seconds. Only
   * `requiresAuth` routes wait for it, and never longer than
   * RESTORE_TIMEOUT_MS.
   */
  const restoreSession = (): Promise<void> => {
    if (restorePromise) return restorePromise;

    restorePromise = (async () => {
      try {
        const supabase = getSupabase();

        // Subscribe before reading: when getSession() times out the refresh can
        // still land afterwards, and this listener is what catches the store up.
        supabase.auth.onAuthStateChange((event, next) => {
          setSession(next);
          // Track OAuth sign-in after the provider redirect completes.
          if (event === 'SIGNED_IN' && next?.user?.app_metadata?.provider !== 'email') {
            const provider = String(next?.user?.app_metadata?.provider ?? 'oauth');
            trackEvent('sign_in', { provider });
            // CA-5: capture signup_completed for OAuth if the account was just created (< 60s).
            const createdAt = next?.user?.created_at;
            if (createdAt && Date.now() - new Date(createdAt).getTime() < 60_000) {
              captureEvent('signup_completed', { method: provider });
            }
          }
        });

        const { data } = await withTimeout(supabase.auth.getSession(), RESTORE_TIMEOUT_MS);
        setSession(data.session);
      } catch {
        // Auth backend unreachable, or Supabase never initialised: stay
        // anonymous rather than stall. A late refresh still reaches the store
        // through the onAuthStateChange listener above.
      } finally {
        sessionRestored.value = true;
      }
    })();

    return restorePromise;
  };

  return {
    session,
    user,
    loading,
    error,
    isAuthenticated,
    sessionRestored,
    signIn,
    signUp,
    signInWithProvider,
    signOut,
    restoreSession
  };
});
